import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  DotsThreeVertical, PencilSimple, Trash, Eye, Copy, ArrowUUpLeft, ClockCounterClockwise, Scales
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

// ---- editable field list ordered by section ----
const FIELDS = [
  // Basic
  ["code", "Material Code", "text", "Basic"],
  ["name", "Material Name", "text", "Basic"],
  ["category", "Category", "text", "Basic"],
  ["unit", "Unit", "select", "Basic", ["kg", "g", "L", "ml", "pc", "sheets"]],
  ["description", "Description", "textarea", "Basic"],
  // Inventory
  ["opening_stock", "Opening Stock", "number", "Inventory"],
  ["stock", "Current Stock", "number", "Inventory"],
  ["min_stock", "Minimum Stock Level", "number", "Inventory"],
  ["max_stock", "Maximum Stock Level", "number", "Inventory"],
  ["reorder_qty", "Reorder Quantity", "number", "Inventory"],
  ["storage_location", "Storage Location", "text", "Inventory"],
  ["shelf_life_days", "Shelf Life (days)", "number", "Inventory"],
  // Procurement
  ["preferred_supplier", "Preferred Supplier", "text", "Procurement"],
  ["alt_suppliers", "Alternative Suppliers", "text", "Procurement"],
  ["cost_per_unit", "Purchase Rate", "number", "Procurement"],
  ["gst_pct", "GST %", "number", "Procurement"],
  ["lead_time_days", "Lead Time (days)", "number", "Procurement"],
  ["last_purchase_price", "Last Purchase Price", "number", "Procurement"],
  // Quality
  ["quality_grade", "Quality Grade", "text", "Quality"],
  ["inspection_required", "Inspection Required", "select", "Quality", ["Yes", "No"]],
  ["qc_parameters", "QC Parameters", "textarea", "Quality"],
  ["batch_tracking", "Batch Number Tracking", "select", "Quality", ["Yes", "No"]],
  ["expiry_tracking", "Expiry Tracking", "select", "Quality", ["Yes", "No"]],
  // Status
  ["status", "Status", "select", "Status", ["active", "inactive"]],
];

const SECTIONS = ["Basic", "Inventory", "Procurement", "Quality", "Status"];

// ---------- Row actions ----------
export function MaterialActions({ mat, onEdit, onView, onDuplicate, onDelete, onRestore, onAdjust, currentUser }) {
  const role = currentUser?.role;
  const canEdit = ["admin", "purchase", "store"].includes(role);
  const canDelete = role === "admin";
  const canAdjust = ["admin", "store", "purchase"].includes(role);
  const isDeleted = mat.status === "deleted";
  return (
    <div className="flex items-center gap-0.5 justify-end">
      <div className="hidden md:flex items-center gap-0.5">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onView(mat)} title="View"
                data-testid={`mat-view-${mat.code}`}><Eye size={15}/></Button>
        {canEdit && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEdit(mat)} title="Edit"
                             data-testid={`mat-edit-${mat.code}`}><PencilSimple size={15}/></Button>}
        {canAdjust && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onAdjust(mat)}
                               title="Stock adjustment" data-testid={`mat-adjust-${mat.code}`}><Scales size={15}/></Button>}
        {canEdit && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onDuplicate(mat)} title="Duplicate"
                             data-testid={`mat-duplicate-${mat.code}`}><Copy size={15}/></Button>}
        {canDelete && !isDeleted && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onDelete(mat)} title="Delete" data-testid={`mat-delete-${mat.code}`}>
            <Trash size={15}/>
          </Button>
        )}
        {canDelete && isDeleted && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => onRestore(mat)} title="Restore" data-testid={`mat-restore-${mat.code}`}>
            <ArrowUUpLeft size={15}/>
          </Button>
        )}
      </div>
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`mat-actions-${mat.code}`}>
              <DotsThreeVertical size={16} weight="bold"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onView(mat)}><Eye size={14} className="mr-2"/>View</DropdownMenuItem>
            {canEdit && <DropdownMenuItem onSelect={() => onEdit(mat)}><PencilSimple size={14} className="mr-2"/>Edit</DropdownMenuItem>}
            {canAdjust && <DropdownMenuItem onSelect={() => onAdjust(mat)}><Scales size={14} className="mr-2"/>Adjust stock</DropdownMenuItem>}
            {canEdit && <DropdownMenuItem onSelect={() => onDuplicate(mat)}><Copy size={14} className="mr-2"/>Duplicate</DropdownMenuItem>}
            <DropdownMenuSeparator/>
            {canDelete && !isDeleted && (
              <DropdownMenuItem onSelect={() => onDelete(mat)} className="text-red-600">
                <Trash size={14} className="mr-2"/>Delete
              </DropdownMenuItem>
            )}
            {canDelete && isDeleted && (
              <DropdownMenuItem onSelect={() => onRestore(mat)} className="text-emerald-600">
                <ArrowUUpLeft size={14} className="mr-2"/>Restore
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ---------- Edit / View dialog ----------
export function MaterialEditDialog({ open, onOpenChange, material, mode = "edit", onSaved }) {
  const [form, setForm] = useState({});
  const [audit, setAudit] = useState([]);
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);
  const readOnly = mode === "view";

  useEffect(() => {
    if (!open || !material) return;
    setForm({ ...material });
    Promise.all([
      api.get(`/materials/${material.id}/audit`).catch(() => ({ data: [] })),
      api.get(`/materials/${material.id}/transactions`).catch(() => ({ data: [] })),
    ]).then(([a, t]) => { setAudit(a.data); setTxns(t.data); });
  }, [open, material]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const patch = { ...form }; delete patch.id;
      await api.put(`/materials/${material.id}`, patch);
      toast.success("Material updated successfully.");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {readOnly ? <><Eye size={17}/>View Material</> : <><PencilSimple size={17}/>Edit Material</>}
            <span className="mono text-xs font-normal text-muted-foreground">· {material?.code}</span>
            {material?.status && material.status !== "active" && (
              <Badge variant="secondary" className="uppercase">{material.status}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {readOnly ? "Read-only view of this raw material's data, stock ledger and version history."
                       : "Update material master data. Changes are audit-logged."}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details" data-testid="mat-tab-details">Details</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="mat-tab-ledger">Stock ledger ({txns.length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="mat-tab-audit"><ClockCounterClockwise size={13} className="mr-1"/>Version history ({audit.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-3">
            {SECTIONS.map(section => (
              <div key={section} className="mb-4">
                <div className="overline mb-2">{section}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {FIELDS.filter(f => f[3] === section).map(([k, label, type, , options]) => (
                    <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
                      <Label className="overline text-[9px]">{label}</Label>
                      {type === "select" ? (
                        <Select value={String(form[k] ?? "")} onValueChange={(v) => set(k, v)} disabled={readOnly}>
                          <SelectTrigger className="h-9 text-sm" data-testid={`mat-field-${k}`}>
                            <SelectValue placeholder="—"/>
                          </SelectTrigger>
                          <SelectContent>
                            {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : type === "textarea" ? (
                        <Textarea value={form[k] ?? ""} onChange={e => set(k, e.target.value)}
                                  disabled={readOnly} rows={2} className="text-sm" data-testid={`mat-field-${k}`}/>
                      ) : (
                        <Input value={form[k] ?? ""}
                               onChange={e => set(k, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
                               disabled={readOnly} type={type === "number" ? "number" : "text"}
                               className={`h-9 text-sm ${type === "number" ? "mono" : ""}`}
                               data-testid={`mat-field-${k}`}/>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="ledger" className="mt-3">
            {txns.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No transactions logged for this material.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-xs uppercase text-muted-foreground">Date</th>
                      <th className="text-left py-2 text-xs uppercase text-muted-foreground">Type</th>
                      <th className="text-right py-2 text-xs uppercase text-muted-foreground">In</th>
                      <th className="text-right py-2 text-xs uppercase text-muted-foreground">Out</th>
                      <th className="text-right py-2 text-xs uppercase text-muted-foreground">Balance</th>
                      <th className="text-left py-2 text-xs uppercase text-muted-foreground">Reason</th>
                      <th className="text-left py-2 text-xs uppercase text-muted-foreground">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map(t => (
                      <tr key={t.id} className="border-b border-border/40 last:border-0" data-testid={`mat-txn-${t.id}`}>
                        <td className="py-2 mono text-[11px]">{new Date(t.at).toLocaleString()}</td>
                        <td className="py-2"><Badge variant="secondary" className="text-[10px]">{t.kind}</Badge></td>
                        <td className="py-2 text-right mono text-emerald-600">{t.qty_in || ""}</td>
                        <td className="py-2 text-right mono text-red-600">{t.qty_out || ""}</td>
                        <td className="py-2 text-right mono font-bold">{t.new_stock}</td>
                        <td className="py-2 text-xs">{t.reason}</td>
                        <td className="py-2 text-[11px] text-muted-foreground">{t.user}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="audit" className="mt-3">
            {audit.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No changes recorded yet.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-auto">
                {audit.map(a => (
                  <div key={a.id} className="border border-border rounded-md p-3 text-xs" data-testid={`mat-audit-${a.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold uppercase text-[10px] tracking-wider">{a.action.replace("_", " ")}</span>
                      <span className="mono text-muted-foreground">{new Date(a.at).toLocaleString()}</span>
                    </div>
                    <div className="mb-1 text-muted-foreground">by {a.user} · {a.role}</div>
                    {a.changes?.reason && (
                      <div className="mb-1 text-amber-800"><span className="font-semibold">Reason:</span> {a.changes.reason}</div>
                    )}
                    {a.diff?.length > 0 && (
                      <div className="grid gap-1 mt-1.5">
                        {a.diff.slice(0, 8).map((d, i) => (
                          <div key={i} className="flex items-baseline gap-2 rounded bg-muted/40 px-2 py-1">
                            <span className="mono text-[10px] font-semibold">{d.field}</span>
                            <span className="text-red-600 line-through truncate">{String(d.before ?? "—").slice(0, 40)}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-emerald-600 truncate">{String(d.after ?? "—").slice(0, 40)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && (
            <Button onClick={save} disabled={busy} data-testid="mat-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Duplicate dialog ----------
export function MaterialDuplicateDialog({ open, onOpenChange, material, onDone }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !material) return;
    setCode(`${material.code}-COPY`);
    setName(`${material.name} (copy)`);
  }, [open, material]);
  const submit = async () => {
    if (!code.trim() || !name.trim()) return toast.error("Code and name are required");
    setBusy(true);
    try {
      const { data } = await api.post(`/materials/${material.id}/duplicate`, { code: code.trim(), name: name.trim() });
      toast.success(`Duplicated as ${data.code}`);
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate Material · {material?.name}</DialogTitle>
          <DialogDescription>Copies category, unit, supplier, stock settings, reorder levels and QC settings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline text-[10px]">New Material Code</Label>
            <Input value={code} onChange={e => setCode(e.target.value)} className="mono" data-testid="mat-dup-code"/>
          </div>
          <div>
            <Label className="overline text-[10px]">New Material Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="mat-dup-name"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="mat-dup-submit">{busy ? "Duplicating…" : "Duplicate"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Delete dialog ----------
export function MaterialDeleteDialog({ open, onOpenChange, material, onDone }) {
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !material) return;
    setUsage(null);
    api.get(`/materials/${material.id}/usage`).then(r => setUsage(r.data)).catch(() => setUsage({ total: 0 }));
  }, [open, material]);
  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/materials/${material.id}`);
      if (data.action === "marked_inactive") toast.success("Material marked Inactive (already used).");
      else if (data.action === "soft_deleted") toast.success("Material moved to inactive.");
      else toast.success("Material deleted.");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const hasUsage = usage && usage.total > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash size={18}/>{hasUsage ? "Mark Material Inactive?" : "Delete Raw Material?"}
          </DialogTitle>
          <DialogDescription>
            {hasUsage
              ? "This material is already linked to recipes / procurement / batches / inventory. You can mark it Inactive to remove it from active lists while preserving history."
              : "This material may already be linked to recipes, procurement, inventory transactions or production batches. Confirm deletion."}
          </DialogDescription>
        </DialogHeader>
        {usage === null ? (
          <div className="text-sm text-muted-foreground py-2">Checking usage…</div>
        ) : hasUsage ? (
          <div className="text-xs space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="font-semibold text-amber-800 mb-1">Referenced in:</div>
            {usage.skus_bom > 0 && <div>• {usage.skus_bom} SKU BOM(s)</div>}
            {usage.purchase_orders > 0 && <div>• {usage.purchase_orders} Purchase order(s)</div>}
            {usage.batches > 0 && <div>• {usage.batches} Production batch(es)</div>}
            {usage.inventory_txns > 0 && <div>• {usage.inventory_txns} Inventory transaction(s)</div>}
          </div>
        ) : (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            No usage in the ERP — safe to delete.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant={hasUsage ? "default" : "destructive"} onClick={doDelete} disabled={busy || usage === null}
                  data-testid="mat-confirm-delete">
            {busy ? "Working…" : hasUsage ? "Mark Inactive" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Stock adjustment dialog ----------
const ADJUST_KINDS = [
  { key: "adjustment", label: "Adjustment (correction)" },
  { key: "wastage",    label: "Wastage" },
  { key: "damage",     label: "Damage" },
  { key: "expiry",     label: "Expiry" },
  { key: "count",      label: "Physical count" },
  { key: "return",     label: "Return" },
];

export function MaterialAdjustDialog({ open, onOpenChange, material, onDone }) {
  const [kind, setKind] = useState("adjustment");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setKind("adjustment"); setDelta(""); setReason(""); } }, [open]);

  const submit = async () => {
    const n = parseFloat(delta);
    if (Number.isNaN(n) || n === 0) return toast.error("Enter a non-zero quantity (+/-).");
    if (!reason.trim()) return toast.error("Reason is required.");
    setBusy(true);
    try {
      await api.post(`/materials/${material.id}/adjust-stock`, { delta: n, kind, reason: reason.trim() });
      toast.success("Stock adjustment saved.");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scales size={17}/>Stock adjustment · {material?.name}</DialogTitle>
          <DialogDescription>Current stock: <span className="mono font-bold">{material?.stock} {material?.unit}</span></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline text-[10px]">Reason type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9" data-testid="mat-adjust-kind"><SelectValue/></SelectTrigger>
              <SelectContent>
                {ADJUST_KINDS.map(k => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-[10px]">Quantity change ({material?.unit}) — use negative for removal</Label>
            <Input type="number" step="0.01" value={delta} onChange={e => setDelta(e.target.value)}
                   placeholder="e.g. -12.5 for wastage · +5 for correction"
                   className="mono h-10 text-base" data-testid="mat-adjust-delta"/>
          </div>
          <div>
            <Label className="overline text-[10px]">Reason (required for audit)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                      placeholder="e.g. 'water damage in warehouse rack B3'"
                      data-testid="mat-adjust-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !delta || !reason.trim()} data-testid="mat-adjust-submit">
            {busy ? "Saving…" : "Save adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
